import R from 'ramda';
import moment from 'moment';
import PropTypes from 'prop-types';
import { formatDate } from "_utils";
import { LICENSE_POOL_PRODUCTS, LICENSE_POOL_TYPES, NEVER, STATUS, SUBLICENSE_DISPLAY_NAMES, SUBLICENSE_OPTIONS_BY_MAX_VERSION } from '../../server/constants';

export const licensePoolSublicenseProps = {
  _id: PropTypes.string.isRequired,
  created_on: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  version: PropTypes.number.isRequired
};

export const licensePoolPropType = PropTypes.shape({
  _id: PropTypes.string.isRequired,
  name: PropTypes.oneOf(Object.values(LICENSE_POOL_PRODUCTS)).isRequired,
  type: PropTypes.oneOf(Object.values(LICENSE_POOL_TYPES)).isRequired,
  status: PropTypes.oneOf(Object.values(STATUS)).isRequired,
  created_on: PropTypes.string.isRequired,
  created_by: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    displayname: PropTypes.string.isRequired,
    gravatar: PropTypes.string.isRequired
  }).isRequired,
  deactivated_on: PropTypes.string,
  deactivated_by: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    displayname: PropTypes.string.isRequired,
    gravatar: PropTypes.string.isRequired
  }),
  ac_count: PropTypes.number.isRequired,
  key_count: PropTypes.number.isRequired,
  ilok_count: PropTypes.number.isRequired,
  keyserver_count: PropTypes.number.isRequired,
  expires_on: PropTypes.string,
  sublicenses: PropTypes.arrayOf(PropTypes.shape(licensePoolSublicenseProps)).isRequired
});

export const isActiveLicensePool = pool => pool.status === STATUS.ACTIVE;
export const isExpiredLicensePool = pool => pool.type === LICENSE_POOL_TYPES.SUBSCRIPTION && moment(pool.expires_on).isBefore(moment());

export const getLicensePoolStatusLabel = pool => {
  if (pool.status === STATUS.NOT_ACTIVE) {
    return 'Deactivated';
  } else if (pool.type === LICENSE_POOL_TYPES.SUBSCRIPTION && moment(pool.expires_on).isBefore(moment())) {
    return 'Expired';
  }
  return 'Active';
}

export const getIsActiveAndNotExpiredLicensePool = pool => isActiveLicensePool(pool) && !isExpiredLicensePool(pool);

export const getMaxExpiryDateForPool = pool => pool && pool.type === LICENSE_POOL_TYPES.SUBSCRIPTION ? pool.expires_on : null;
export const getMaxExpiresDaysAfterInitForPool = pool => pool && pool.type === LICENSE_POOL_TYPES.SUBSCRIPTION ? R.clamp(1, 365, -1 * moment().diff(moment(pool.expires_on), 'days')) : Infinity;
export const getIsLicensePoolWithCount = pool => pool.ac_count + pool.key_count + pool.ilok_count + pool.keyserver_count > 0;

export const getLicensePoolExpiresOnLabel = pool => {
  if (pool.type === LICENSE_POOL_TYPES.FULL) {
    return 'never';
  } else if (pool.type === LICENSE_POOL_TYPES.SUBSCRIPTION) {
    return formatDate(pool.expires_on);
  }
  return '';
};

const productLabelLookup = {
  [LICENSE_POOL_PRODUCTS.MAX7]: 'Max 7',
  [LICENSE_POOL_PRODUCTS.MAX8]: 'Max 8',
  [LICENSE_POOL_PRODUCTS.MAX9]: 'Max 9',
};

const typeLabelLookup = {
  [LICENSE_POOL_TYPES.FULL]: 'Permanent',
  [LICENSE_POOL_TYPES.SUBSCRIPTION]: 'Subscription'
};

export const getLicensePoolTypeLabel = pool => {
  return typeLabelLookup[pool.type] || pool.type;
}

export const getLicensePoolProductLabel = pool => {
  return pool.type === LICENSE_POOL_TYPES.FULL
    ? `${productLabelLookup[pool.name] || pool.name} ${typeLabelLookup[pool.type] || pool.type}`
    : `Max ${typeLabelLookup[pool.type] || pool.type}`
}

export const getLicensePoolSublicensesLabel = pool => {
  // Display quick summary of addons licenses, no expiry for license pools as it inherits from the parent pool
  if (!pool.sublicenses || !pool.sublicenses.length) return '';

  const summary = pool.sublicenses
    .map((sl) => `${SUBLICENSE_DISPLAY_NAMES[sl.name]}`)
    .join('; ');

  return summary || '';
};

export const getLicensePoolSublicensesLabelWithPrefix = pool => {
  const label = getLicensePoolSublicensesLabel(pool);
  return label && label.length ? `incl. ${label}` : '';
}

export const getACLicensePoolSelectionOptions = pools => pools.map(pool => {
  // Disregard inactive, expired or empty license pools
  if (
    !getIsActiveAndNotExpiredLicensePool(pool) ||
    pool.ac_count <= pool.usage.activation_codes.active.total
  ) {
    return false;
  }

  const info = [
    `| ${pool.ac_count - pool.usage.activation_codes.active.total} of ${pool.ac_count} available`
  ];

  if (pool.expires_on && new Date(pool.expires_on).toUTCString() !== NEVER) {
    info.push(`expires ${moment(pool.expires_on).format("MM/DD/YYYY")}`);
  };

  if (pool.description && pool.description.length) {
    info.push(pool.description.trim());
  }

  return {
    text: [getLicensePoolProductLabel(pool), getLicensePoolSublicensesLabelWithPrefix(pool), info.join(" | ")].join(" "),
    value: pool._id,
  };
}).filter(Boolean);

export const getKeyLicensePoolSelectionOptions = pools => pools.map(pool => {
  // Disregard inactive, expired or empty license pools
  if (!getIsActiveAndNotExpiredLicensePool(pool) || pool.key_count < 1) {
    return false;
  }

  const info = [
    `| ${pool.key_count} ${pool.key_count > 1 ? 'seats' : 'seat'}`
  ];

  if (pool.expires_on && new Date(pool.expires_on).toUTCString() !== NEVER) {
    info.push(`expires ${moment(pool.expires_on).format("MM/DD/YYYY")}`);
  };

  if (pool.description && pool.description.length) {
    info.push(pool.description.trim());
  }

  return {
    text: [getLicensePoolProductLabel(pool), getLicensePoolSublicensesLabelWithPrefix(pool), info.join(" | ")].join(" "),
    value: pool._id,
  };
}).filter(Boolean);

export const getLicensePoolAddSublicenseNameSelectionOptions = pool => {
  const version = pool.name;
  const availAddons = SUBLICENSE_OPTIONS_BY_MAX_VERSION[version];

  const existingSubLics = pool.sublicenses.map(sl => sl.name);

  return availAddons.map(ad => ({
    text: SUBLICENSE_DISPLAY_NAMES[ad],
    value: ad,
    disabled: existingSubLics.includes(ad)
  }));
};



// WEBPACK FOOTER //
// ./webAdmin/utils/licensePools.js